import { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, CircularProgress, Button, Tooltip } from "@material-ui/core";
import { min, max } from "lodash";
import { formatDateTime, parseDateTime } from "../helpers/time";
import history from "../helpers/history";

import { useSelectorTyped as useSelector, useThunkDispatch } from "../redux/common";
import {
  queryDetectionSummary,
  queryDetections,
  cancelCurrentQuery,
  fetchDetectionCountsHistogram,
  queryAbacus,
  clearDetectionData,
  getDetCountApi,
  resetTable,
} from "../redux/detections/detection-actions";
import { DetectionFilterInput } from "../redux/detections/detection-types";
import { listFilesApi } from "../redux/files/files-actions";
import { FileInfo } from "../redux/files/files-types";
import { readDeploymentList } from "../redux/deployments/deployments-actions";

import { SpinnerInnovasea } from "../fathom-brella";
import ResizableSplitPanel from "../components/common/ResizableSplitPanel";
import DateTimePicker from "../components/common/DateTimePicker";
import FlexCol from "../components/common/FlexCol";
import FlexRow from "../components/common/FlexRow";
import useFileProcessingWarning from "../components/hooks/useFileProcessingWarning";
import HelpPopover from "../components/common/HelpPopover";
import { SubTitle, Title } from "../components/common/typography";
import { AutocompleteChips, AutoCompleteOptions } from "../components/common/inputs";
import { DetectionVisualizer } from "../components/detection/DetectionVisualizer";
import { AbacusPlot, isFeatureEnabled } from "../components/common/ExperimentalFeaturesDialog";
import moment from "moment";
import { readAnimalList } from "../redux/animals/animals-actions";

type TxSelection = {
  displayId: string;
  count: number;
};
type RxSelection = {
  serial: string;
  rxName: string;
  count: number;
};
type RxList = (RxSelection & { idCounts: any[] })[];
/** time comparison is done using ISOString because it's faster */
type TimeRange = {
  min: string;
  max: string;
};

/** reference time for calculating min date. Doesn't matter that it's not exactly "now" */
const nowIsh = new Date().toISOString();

const useStyles = makeStyles(theme => ({
  root: {
    height: "100%",
    width: "100%",
  },
  table: {
    height: "100%",
    width: "100%",
  },
  paper: {
    maxWidth: "500px",
    padding: theme.spacing(2),
  },
  submitBtn: {
    margin: theme.spacing(1),
  },
  countProgress: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  cancelBtn: { width: 100, marginTop: theme.spacing(1) },
}));

function Detections() {
  const classes = useStyles();
  const dispatch = useThunkDispatch();

  // Feature flag
  const abacusEnabled = isFeatureEnabled(AbacusPlot);

  // STATE
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [displayMode, setDisplayMode] = useState<"table" | "abacus">("table");
  const [selectedRxs, setSelectedRxs] = useState([] as RxSelection[]);
  const [selectedTxs, setSelectedTxs] = useState([] as TxSelection[]);
  const [filtersChanged, setFiltersChanged] = useState<boolean>(true);
  const [binHours, setBinHours] = useState<24 | 1>(24);
  const [initialLoad, setInitialLoad] = useState(true);

  // STORE
  const { selectedWorkspaceId } = useSelector(({ workspaces }) => {
    const selectedWorkspace: any = workspaces?.selectedWorkspace || {};
    return { selectedWorkspaceId: selectedWorkspace.id };
  });

  const { selectedStudyId, studyFiles, studyDeployments } = useSelector(({ study }) => {
    const selectedStudy =
      (study?.selectedId && study?.studies?.find((s: any) => s.id === study.selectedId)) || {};
    const studyFiles: string[] = selectedStudy?.files?.map(file => file.name) || [];
    const studyDeployments: string[] = selectedStudy?.deployments?.map(({ id }) => id) || [];
    const selectedStudyId = selectedStudy.id;
    return { selectedStudyId, studyFiles, studyDeployments };
  });

  const { deploymentsTimeRange, deploymentsRxs } = useSelector(({ deployments }) => {
    const rxTimeRangeMap = {} as { [serial: string]: TimeRange };
    const deploymentsRxs: string[] = [];
    deployments.deployments?.forEach(deployment => {
      if (!selectedStudyId || studyDeployments.includes(deployment.id)) {
        deployment?.deviceAttachments?.forEach(da => {
          if (da.device?.capabilities?.includes("RECEIVER")) {
            const serial = da.device.serial;
            const min = da.start || deployment.start;
            const max = da.end || deployment.end;

            // get min / max deployment time per rx
            if (!rxTimeRangeMap[serial]) {
              rxTimeRangeMap[serial] = { min: nowIsh, max: "" };
            }
            // there may be more than one deployment of a given receiver, so pick the earliest start and latest end
            rxTimeRangeMap[serial].min =
              rxTimeRangeMap[serial].min < min ? rxTimeRangeMap[serial].min : min;
            rxTimeRangeMap[serial].max =
              rxTimeRangeMap[serial].max > max ? rxTimeRangeMap[serial].max : max;

            // also add to list of rx in deployments:
            deploymentsRxs.push(serial);
          }
        });
      }
    });

    // reduce min / max deployment time per rx to only one min/max
    const deploymentsTimeRange: TimeRange = { min: nowIsh, max: "" };
    for (const serial in rxTimeRangeMap) {
      deploymentsTimeRange.min =
        deploymentsTimeRange.min < rxTimeRangeMap[serial].min
          ? deploymentsTimeRange.min
          : rxTimeRangeMap[serial].min;
      deploymentsTimeRange.max =
        deploymentsTimeRange.max > rxTimeRangeMap[serial].max
          ? deploymentsTimeRange.max
          : rxTimeRangeMap[serial].max;
    }
    return { deploymentsTimeRange, deploymentsRxs };
  });

  function minMaxTimesFilelist(fileList: FileInfo[]): TimeRange {
    const filesTimeRange: TimeRange = {
      min: nowIsh,
      max: "",
    };
    fileList.forEach(({ rxLogProperties }) => {
      if (rxLogProperties) {
        if (rxLogProperties.maxRecordTime > filesTimeRange.max) {
          filesTimeRange.max = rxLogProperties.maxRecordTime;
        }
        /**
         * If we include detections with epoch = 0 (1970-01-01) query performance
         * is way worse because it's creating hourly bins from the epoch 0 to the actual
         * start time of the data. Often creating 40+ years of empty bins
         */
        if (
          rxLogProperties.minRecordTime < filesTimeRange.min &&
          rxLogProperties.minRecordTime > "1970-01-01T00:00:00Z"
        ) {
          filesTimeRange.min = rxLogProperties.minRecordTime;
        }
      }
    });
    return filesTimeRange;
  }

  const { filesLoaded, fileList, filesRxs, filesHasHR, filesTimeRange } = useSelector(
    ({ files }) => {
      const fileList: FileInfo[] = [];
      const filesLoaded = files.isLoaded;
      const deploymentsFiles = files.fileList
        .filter(f => deploymentsRxs.includes(f.rxLogProperties?.serial || ""))
        .map(f => f.name);
      for (const file of files.fileList) {
        if (
          !selectedStudyId ||
          studyFiles.includes(file.name) ||
          deploymentsFiles.includes(file.name)
        ) {
          fileList.push(file);
        }
      }
      const filesRxs = fileList.map(file => file.rxLogProperties?.serial);
      const filesHasHR = fileList.some(file => file.rxLogProperties?.counts.hrExternalAccepted);

      return {
        filesLoaded,
        fileList,
        filesRxs,
        filesHasHR,
        filesTimeRange: minMaxTimesFilelist(fileList),
      };
    }
  );

  const {
    summaryLoading,
    summaryError,
    detectionRows,
    detectionState,
    detectionHistogram,
    detectionAbacus,
    rxs,
    count,
  } = useSelector(({ detection }) => {
    const summaryLoading = detection.summary.loading;
    const summaryError = detection.summary.error;
    const detectionSummary = detection.summary.detectionSummary;
    const rxs: RxList = [];
    if (!summaryLoading && !summaryError && detectionSummary) {
      // console.time("parse-detectionSummary");
      detectionSummary.receiverSummary.forEach(rs => {
        const serial = rs.receiver.serial;
        if (filesRxs.includes(serial) || deploymentsRxs.includes(serial)) {
          const model = fileList.find(fi => fi.rxLogProperties?.serial === serial)?.rxLogProperties
            ?.model;
          rxs.push({
            count: rs.count,
            rxName: model ? `${model.split("-")[0]}-${serial}` : serial,
            serial,
            idCounts: rs.idCounts,
          });
        }
      });

      //First, we check which receivers were included by searching detections table.
      //Then, we check which receivers were left out for not having any detections
      const rxsFromDetections = rxs.map(rx => rx.serial);
      //filters out undefined elements, changing the type to just string[]
      //ensuring only files with serials are considered
      const noDetectionSerials = filesRxs.flatMap(rx =>
        rx && !rxsFromDetections.includes(rx) ? [rx] : []
      );
      noDetectionSerials.forEach(serial => {
        const model = fileList.find(fi => fi.rxLogProperties?.serial === serial)?.rxLogProperties
          ?.model;
        rxs.push({
          count: 0,
          rxName: model ? `${model.split("-")[0]}-${serial}` : serial,
          serial,
          idCounts: [],
        });
      });
      // console.timeEnd("parse-detectionSummary");
    }

    return {
      summaryLoading,
      summaryError,
      detectionRows: detection.table.rows,
      detectionState: detection.table.status,
      detectionHistogram: detection.histogram,
      detectionCountHistogram: detection.histogram?.countPerInterval,
      detectionAbacus: detection.abacus,
      rxs,
      count: detection.count,
    };
  });

  const { animalTransmittersMap, animalLoading, animalError, animalNamesMap } = useSelector(
    ({ animals }) => {
      const animalTransmitters = animals.animals?.flatMap(an =>
        an.devices.flatMap(dev => dev.transmitters.map(tx => tx.displayId))
      );
      const animalNames = animals.animals?.map(an => [
        an.name,
        ...an.devices.flatMap(dev => dev.transmitters.map(tx => tx.displayId)),
      ]);
      const animalTransmittersMap = new Map<string, number>();
      const animalNamesMap = new Map<string, string[]>();
      animalTransmitters?.forEach(txd =>
        animalTransmittersMap.set(txd, (animalTransmittersMap.get(txd) || 0) + 1)
      );
      animalNames?.forEach(an =>
        an.slice(1).forEach(txd => {
          if (an[0]) animalNamesMap.set(txd, [...(animalNamesMap.get(txd) || []), an[0]]);
        })
      );
      const animalLoading = animals.loading;
      const animalError = animals.error;
      return { animalTransmittersMap, animalLoading, animalError, animalNamesMap };
    }
  );

  const totalCount = rxs.map(r => r.count).reduce((acc, val) => (acc += val), 0);
  const detCount = initialLoad ? totalCount : count.count;
  const tableLimitReached = detCount > 1000000; // 1 Mill

  function getSelectableTxs(rxs: RxList) {
    const selRxSerials = selectedRxs.length ? selectedRxs.map(sr => sr.serial) : [];
    const rxTemp: RxList = rxs.filter(r =>
      selRxSerials.length ? selRxSerials.includes(r.serial) : true
    );
    const txIdCounts: { [displayId: string]: number } = {};
    rxTemp.forEach(r => {
      r.idCounts.forEach(({ displayId, count }) => {
        if (txIdCounts[displayId] === undefined) {
          txIdCounts[displayId] = 0;
        }
        txIdCounts[displayId] += count;
      });
    });
    const selectableTxs: TxSelection[] = Object.keys(txIdCounts).map(displayId => ({
      displayId,
      count: txIdCounts[displayId],
    }));
    return selectableTxs.sort((t1, t2) => t2.count - t1.count);
  }
  const txs = getSelectableTxs(rxs);

  const filters: DetectionFilterInput = {
    includeReceiverSerials: selectedRxs?.map(({ serial }) => serial),
    includeTransmitterIDs: selectedTxs?.map(({ displayId }) => displayId),
    includeStartTime: (start && parseDateTime(start)) || undefined,
    includeEndTime: (end && parseDateTime(end)) || undefined,
  };

  function resetState() {
    setStart("");
    setEnd("");
    setSelectedRxs([]);
    setSelectedTxs([]);
    setInitialLoad(true);
  }
  function getBinHours() {
    // Switch binning based on selected time range
    const selectedSerials = selectedRxs?.map(({ serial }) => serial);
    const selectedFileList = fileList.filter(f =>
      selectedSerials.includes(f?.rxLogProperties?.serial?.toString() || "")
    );
    const selectedTimeRange =
      selectedRxs?.length > 0
        ? minMaxTimesFilelist(selectedFileList)
        : minMaxTimesFilelist(fileList);
    const periodGtYear =
      moment(selectedTimeRange.max).diff(moment(selectedTimeRange.min), "years") > 1;
    return periodGtYear ? 24 : 1;
  }
  function applyFilters() {
    const adjustedBinHours = getBinHours();
    setBinHours(adjustedBinHours);
    dispatch(cancelCurrentQuery());
    dispatch(fetchDetectionCountsHistogram(filters, adjustedBinHours));
    dispatch(queryAbacus(filters, adjustedBinHours));
    setFiltersChanged(false);
    if (!tableLimitReached) dispatch(queryDetections(filters));
  }

  // changing workspace || study and initital load
  useEffect(() => {
    resetState();
    dispatch(clearDetectionData());
    dispatch(queryDetectionSummary());
    dispatch(listFilesApi());
    dispatch(readDeploymentList());
    dispatch(readAnimalList());
    dispatch(cancelCurrentQuery());
  }, [dispatch, selectedWorkspaceId, selectedStudyId]);

  // Set initial time range:
  useEffect(() => {
    const _start = min([filesTimeRange.min, deploymentsTimeRange.min]) as string;
    const _end = max([filesTimeRange.max, deploymentsTimeRange.max]) as string;

    if (_start != nowIsh) {
      setStart(formatDateTime(_start));
    }
    if (_end != "") {
      setEnd(formatDateTime(_end));
    }
  }, [filesTimeRange.min, filesTimeRange.max, deploymentsTimeRange.min, deploymentsTimeRange.max]);

  useFileProcessingWarning();

  if (summaryLoading || !filesLoaded || animalLoading) {
    return (
      <div className={classes.root}>
        <SpinnerInnovasea text={"Preparing detection database"} />
      </div>
    );
  }

  if (summaryError || animalError) {
    history.push("/error");
    return null;
  }

  function filterChange(field?: "start" | "end" | "selectedRxs" | "selectedTxs", value?: any) {
    if (field === "start") {
      setStart(value);
      filters.includeStartTime = parseDateTime(value);
    } else if (field === "end") {
      setEnd(value);
      filters.includeEndTime = parseDateTime(value);
    } else if (field === "selectedRxs") {
      const newRxs = rxs.filter(rx => value.includes(rx.serial));
      setSelectedRxs(newRxs);
      filters.includeReceiverSerials = newRxs.map(r => r.serial);
    } else if (field === "selectedTxs") {
      const newTxs = txs.filter(tx => value.includes(tx.displayId));
      setSelectedTxs(newTxs);
      filters.includeTransmitterIDs = newTxs.map(t => t.displayId);
    }
    dispatch(resetTable());
    dispatch(getDetCountApi(filters));
    setInitialLoad(false);
    setFiltersChanged(true);
  }

  const rxOptions: AutoCompleteOptions[] = rxs.map(rx => ({
    key: rx.serial,
    label: rx.rxName,
    labelSuffix: (
      <Typography variant="caption" style={{ padding: 5 }}>
        (DETS: {rx.count})
      </Typography>
    ),
  }));

  function generateTooltipTitle(txDisplayId: string) {
    const hasAnimalNames = animalNamesMap.has(txDisplayId);
    const totalAnimals = animalTransmittersMap.get(txDisplayId)!;
    let title = "";
    if (hasAnimalNames) {
      const animalNames = animalNamesMap.get(txDisplayId)!;
      const numberOfNamedAnimals = animalNames.length;
      const moreThanOneNamedAnimal = numberOfNamedAnimals > 1;
      const numberOfUnnamedAnimals = totalAnimals - numberOfNamedAnimals;
      const moreThanOneUnnamedAnimals = numberOfUnnamedAnimals > 1;
      if (moreThanOneNamedAnimal)
        title +=
          animalNames.slice(0, numberOfNamedAnimals - 1).join(", ") +
          " and " +
          animalNames[numberOfNamedAnimals - 1];
      else title += animalNames[0];
      if (numberOfUnnamedAnimals !== 0)
        title +=
          " and " +
          numberOfUnnamedAnimals +
          " other animal" +
          (moreThanOneUnnamedAnimals ? "s" : "");
    } else {
      const moreThanOneTotalAnimals = totalAnimals > 1;
      title +=
        totalAnimals +
        " animal" +
        (moreThanOneTotalAnimals ? "s are " : " is ") +
        "associated with this ID";
    }
    return title;
  }

  const txOptions: AutoCompleteOptions[] = txs.map(tx => ({
    key: tx.displayId,
    label: tx.displayId,
    labelSuffix: (
      <Typography variant="caption" style={{ padding: 5 }}>
        (DETS: {tx.count})
        {animalTransmittersMap.has(tx.displayId) ? (
          <Tooltip title={`${generateTooltipTitle(tx.displayId)}`}>
            <img
              style={{ verticalAlign: "middle", marginLeft: 5 }}
              src="/img/animals.svg"
              width={25}
            />
          </Tooltip>
        ) : (
          <span style={{ marginRight: 30 }}></span>
        )}
      </Typography>
    ),
  }));
  console.log({ rowsLength: detectionRows.length });
  return (
    <div className={classes.root}>
      <ResizableSplitPanel
        direction="horizontal"
        firstInit="30%"
        firstMin={300}
        firstContent={
          <FlexCol fullWidth fullHeight paddingLevel={2}>
            <Title>Filters</Title>
            <FlexCol paddingLevel={1}>
              <SubTitle>Devices</SubTitle>
              <AutocompleteChips
                options={rxOptions}
                selection={selectedRxs.map(({ rxName, serial }) => ({
                  key: serial,
                  label: rxName,
                }))}
                selectionHandler={selection =>
                  filterChange(
                    "selectedRxs",
                    selection.map(s => s.key)
                  )
                }
                label={`Receivers in ${(selectedStudyId && "selected study") || "workspace"}`}
                placeholder="Filter detections by receiver(s)"
              />
              <AutocompleteChips
                options={txOptions}
                selection={selectedTxs.map(({ displayId }) => ({
                  key: displayId,
                  label: displayId,
                }))}
                selectionHandler={selection =>
                  filterChange(
                    "selectedTxs",
                    selection.map(s => s.key)
                  )
                }
                label={`Transmitters`}
                placeholder="Filter detections by transmitter(s)"
              />
              {filesHasHR && (
                <FlexRow vAlign="center" hAlign="right">
                  <Typography variant="caption">HR Detections</Typography>
                  <HelpPopover
                    tooltip="Where are my HR detections?"
                    helpContent={
                      "Support for working with detection data from HR2 and HR3 receivers is under development"
                    }
                  />
                </FlexRow>
              )}
            </FlexCol>
            <FlexCol paddingLevel={1}>
              <SubTitle>Time Frame</SubTitle>
              <FlexCol itemSpacing={1.5} paddingLevel={1.5} style={{ paddingTop: 0 }}>
                <DateTimePicker
                  label="Start Time"
                  value={start}
                  onChange={(value: string) => filterChange("start", value)}
                  helperText={"Limit to detections after this time"}
                />
                <DateTimePicker
                  label="End Time"
                  value={end}
                  onChange={(value: string) => filterChange("end", value)}
                  helperText={"Limit to detections before this time"}
                />
              </FlexCol>
            </FlexCol>
            <FlexCol hAlign="center" paddingLevel={2} itemSpacing={2}>
              {filtersChanged && (
                <Tooltip
                  title={
                    tableLimitReached && displayMode === "table"
                      ? "Please filter down to less than 1,000,000 detections"
                      : ""
                  }
                  placement="top"
                >
                  <span>
                    <Button
                      onClick={applyFilters}
                      variant="outlined"
                      disabled={(tableLimitReached && displayMode === "table") || count.loading}
                    >
                      Apply Filters
                    </Button>
                  </span>
                </Tooltip>
              )}
              {count.loading || summaryLoading ? (
                <CircularProgress size={18} />
              ) : (
                <SubTitle style={{ margin: 8 }}>
                  {` ${
                    initialLoad ? totalCount.toLocaleString() : count.count?.toLocaleString()
                  } detections in selected filters`}
                </SubTitle>
              )}
            </FlexCol>
          </FlexCol>
        }
        secondContent={
          <DetectionVisualizer
            binHours={binHours}
            tableLimitReached={tableLimitReached}
            abacusEnabled={abacusEnabled}
            displayMode={displayMode}
            setDisplayMode={setDisplayMode}
            detectionState={detectionState}
            detectionRows={detectionRows}
            detectionCount={detCount}
            histogram={detectionHistogram}
            abacus={detectionAbacus}
          />
        }
      />
    </div>
  );
}

export default Detections;
